import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { notificationsAPI } from '../../services/api';

const Navbar = ({ user, onMenuClick, onLogout, bannerOffset = 0, isTrialActive, trialExpiringSoon, trialDaysLeft, trialEndsAt }) => {
    const { toggleTheme, isDark } = useTheme();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [navSearch, setNavSearch] = useState('');
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    // Notification State
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loadingNotifications, setLoadingNotifications] = useState(false);

    const userMenuRef = useRef(null);
    const notifMenuRef = useRef(null);
    const searchInputRef = useRef(null);
    const navigate = useNavigate();

    // Fetch unread count on mount
    useEffect(() => {
        fetchUnreadCount();
        // Optional: Poll every minute
        const interval = setInterval(fetchUnreadCount, 60000);
        return () => clearInterval(interval);
    }, []);

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (notificationsOpen) {
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationsOpen]);

    const fetchUnreadCount = async () => {
        try {
            const response = await notificationsAPI.getUnreadCount();
            setUnreadCount(response.data.count);
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoadingNotifications(true);
        try {
            const response = await notificationsAPI.getNotifications({ limit: 10 });
            setNotifications(response.data.notifications);
            // Update unread count as well to ensure sync
            fetchUnreadCount();
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationsAPI.markAsRead(id);
            // Update local state
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationsAPI.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        // Handle navigation based on type later if needed
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'lead_auto_responded': return 'smart_toy';
            case 'lead_replied': return 'chat';
            case 'system_alert': return 'warning';
            default: return 'notifications';
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'lead_auto_responded': return 'text-cyan';
            case 'lead_replied': return 'text-emerald-500';
            case 'system_alert': return 'text-amber-500';
            default: return isDark ? 'text-light' : 'text-darkTeal';
        }
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
            if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) {
                setNotificationsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when opening
    useEffect(() => {
        if (mobileSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [mobileSearchOpen]);

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleSearch = () => {
        if (navSearch.trim()) {
            navigate(`/leads?search=${encodeURIComponent(navSearch.trim())}`);
            setNavSearch('');
            setMobileSearchOpen(false);
        }
    };

    return (
        <header className={`fixed top-0 left-0 right-0 h-14 md:h-16 z-50 flex items-center justify-between px-4 md:px-6 border-b backdrop-blur-md transition-all duration-300 ${isDark ? 'bg-navy/80 border-light/10' : 'bg-mint/80 border-teal/10'}`} style={bannerOffset ? { top: `${bannerOffset}px` } : {}}>

            {/* Mobile Search Overlay */}
            {mobileSearchOpen ? (
                <div className={`absolute inset-0 z-50 flex items-center px-4 ${isDark ? 'bg-navy' : 'bg-mint'}`}>
                    <div className="relative flex-1">
                        <span className={`material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>search</span>
                        <input
                            ref={searchInputRef}
                            className={`w-full rounded-full py-2 pl-10 pr-4 text-sm focus:ring-1 transition-all ${isDark
                                ? 'bg-navy border border-light/20 focus:ring-cyan focus:border-cyan text-light placeholder-light/40'
                                : 'bg-mint border border-teal/30 focus:ring-primary focus:border-primary text-darkTeal placeholder-darkTeal/40'
                                }`}
                            placeholder="Search leads..."
                            type="text"
                            value={navSearch}
                            onChange={(e) => setNavSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                    </div>
                    <button
                        onClick={() => setMobileSearchOpen(false)}
                        className={`ml-3 p-2 rounded-full ${isDark ? 'text-light/70 hover:bg-light/10' : 'text-darkTeal/70 hover:bg-teal/10'}`}
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            ) : (
                <>
                    {/* Left: Logo + Mobile Menu */}
                    <div className="flex items-center gap-1.5 md:gap-2 w-auto md:w-64">
                        {/* Mobile hamburger - Hidden now as we have bottom nav */}
                        <button
                            onClick={onMenuClick}
                            className={`hidden p-1 mr-2 ${isDark ? 'text-light/50 hover:text-light' : 'text-darkTeal/50 hover:text-darkTeal'}`}
                        >
                            <span className="material-symbols-outlined">menu</span>
                        </button>
                        <Link to="/" className="flex items-center gap-1.5 md:gap-2 hover:opacity-80 transition-opacity">
                            <span className={`material-symbols-outlined text-2xl md:text-3xl ${isDark ? 'text-cyan' : 'text-primary'}`} style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
                            <h1 className={`text-lg md:text-xl font-bold tracking-tight ${isDark ? 'text-cyan' : 'text-teal'}`}>OffTrigger</h1>
                        </Link>
                    </div>

                    {/* Center: Search (Desktop) */}
                    <div className="flex-1 max-w-md mx-4 hidden md:block">
                        <div className="relative group">
                            <span className={`material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-light/50 group-focus-within:text-cyan' : 'text-darkTeal/50 group-focus-within:text-teal'}`}>search</span>
                            <input
                                className={`w-full rounded-full py-2 pl-10 pr-4 text-sm focus:ring-1 transition-all ${isDark
                                    ? 'bg-navy border border-light/20 focus:ring-cyan focus:border-cyan text-light placeholder-light/40'
                                    : 'bg-mint border border-teal/30 focus:ring-primary focus:border-primary text-darkTeal placeholder-darkTeal/40'
                                    }`}
                                id="global-search-input"
                                placeholder="Search leads, properties..."
                                type="text"
                                value={navSearch}
                                onChange={(e) => setNavSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearch();
                                }}
                            />
                        </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 md:gap-4">
                        {/* Trial Banner — always visible during trial */}
                        {isTrialActive && (
                            <button
                                onClick={() => navigate('/billing')}
                                className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${trialExpiringSoon
                                    ? (isDark
                                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20 animate-pulse'
                                        : 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100 animate-pulse')
                                    : (isDark
                                        ? 'bg-cyan/10 border-cyan/30 text-cyan hover:bg-cyan/20'
                                        : 'bg-teal/10 border-teal/30 text-teal hover:bg-teal/20')
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">{trialExpiringSoon ? 'warning' : 'schedule'}</span>
                                <span>
                                    Trial ends {trialEndsAt ? new Date(trialEndsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `in ${trialDaysLeft}d`}
                                </span>
                                <span className={`font-black ${trialExpiringSoon ? (isDark ? 'text-amber-500' : 'text-amber-700') : (isDark ? 'text-cyan' : 'text-primary')}`}>· Upgrade</span>
                            </button>
                        )}

                        {/* Mobile Search Icon */}
                        <button
                            className={`md:hidden p-2 rounded-full transition-colors ${isDark ? 'hover:bg-light/10 text-light' : 'hover:bg-teal/10 text-darkTeal'}`}
                            onClick={() => setMobileSearchOpen(true)}
                        >
                            <span className="material-symbols-outlined">search</span>
                        </button>

                        {/* Theme toggle - Hidden on mobile, moved to dropdown */}
                        <button
                            className={`hidden md:flex p-2 rounded-full transition-colors ${isDark ? 'hover:bg-light/10 text-light' : 'hover:bg-teal/10 text-darkTeal'}`}
                            onClick={toggleTheme}
                            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            <span className="material-symbols-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
                        </button>

                        {/* Notifications */}
                        <div className="relative" ref={notifMenuRef}>
                            <button
                                className={`p-2 rounded-full relative transition-colors ${isDark ? 'hover:bg-light/10 text-light' : 'hover:bg-teal/10 text-darkTeal'}`}
                                onClick={() => { setNotificationsOpen(!notificationsOpen); setUserMenuOpen(false); }}
                            >
                                <span className="material-symbols-outlined">notifications</span>
                                {unreadCount > 0 && (
                                    <span className={`absolute top-2 right-2 w-2.5 h-2.5 rounded-full border-2 ${isDark ? 'bg-pink border-navy' : 'bg-primary border-mint'}`}></span>
                                )}
                            </button>
                            {/* Notifications dropdown */}
                            {notificationsOpen && (
                                <div className={`fixed inset-x-4 top-16 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 w-auto md:w-80 rounded-xl shadow-lg overflow-hidden z-50 border ${isDark ? 'bg-navy border-light/20' : 'bg-mint border-teal/20'}`}>
                                    <div className={`p-4 border-b flex justify-between items-center ${isDark ? 'border-light/10' : 'border-teal/10'}`}>
                                        <h4 className={`font-bold text-sm ${isDark ? 'text-light' : 'text-darkTeal'}`}>Notifications</h4>
                                        <button
                                            onClick={markAllAsRead}
                                            className={`text-xs hover:underline ${isDark ? 'text-cyan' : 'text-primary'}`}
                                        >
                                            Mark all read
                                        </button>
                                    </div>
                                    <div className="max-h-80 overflow-y-auto">
                                        {loadingNotifications ? (
                                            <div className={`p-8 text-center text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                                Loading...
                                            </div>
                                        ) : notifications.length === 0 ? (
                                            <div className={`p-8 text-center text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                                No notifications
                                            </div>
                                        ) : (
                                            notifications.map(notification => (
                                                <div
                                                    key={notification.id}
                                                    onClick={() => handleNotificationClick(notification)}
                                                    className={`p-4 transition-colors cursor-pointer border-b ${isDark ? 'hover:bg-light/5 border-light/5' : 'hover:bg-teal/5 border-teal/5'} ${!notification.read ? (isDark ? 'bg-light/5' : 'bg-teal/5') : ''}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <span className={`material-symbols-outlined text-lg mt-0.5 ${getNotificationColor(notification.type)}`}>
                                                            {getNotificationIcon(notification.type)}
                                                        </span>
                                                        <div className="flex-1">
                                                            <p className={`text-sm ${isDark ? 'text-light' : 'text-darkTeal'} ${!notification.read ? 'font-semibold' : ''}`}>
                                                                {notification.title}
                                                            </p>
                                                            <p className={`text-xs mt-1 ${isDark ? 'text-light/70' : 'text-darkTeal/70'}`}>
                                                                {notification.message}
                                                            </p>
                                                            <p className={`text-[10px] mt-2 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                                                {formatTimeAgo(notification.created_at)}
                                                            </p>
                                                        </div>
                                                        {!notification.read && (
                                                            <div className={`w-2 h-2 rounded-full mt-1.5 ${isDark ? 'bg-cyan' : 'bg-primary'}`}></div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    <div className={`p-3 text-center border-t ${isDark ? 'border-light/10' : 'border-teal/10'}`}>
                                        <button
                                            onClick={() => {
                                                navigate('/notifications');
                                                setNotificationsOpen(false);
                                            }}
                                            className={`text-xs font-semibold uppercase tracking-wider transition-colors ${isDark ? 'text-cyan hover:text-cyan/80' : 'text-teal hover:text-primary'}`}
                                        >
                                            View All
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* User Profile + Dropdown */}
                        <div className="relative" ref={userMenuRef}>
                            <div
                                className={`flex items-center gap-3 pl-2 border-l ml-2 cursor-pointer ${isDark ? 'border-light/20' : 'border-teal/20'}`}
                                onClick={() => { setUserMenuOpen(!userMenuOpen); setNotificationsOpen(false); }}
                            >
                                <div className="text-right hidden md:block">
                                    <p className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{user?.full_name || 'Alex Morgan'}</p>
                                    <p className={`text-xs ${isDark ? 'text-cyan' : 'text-teal'}`}>Top Agent</p>
                                </div>
                                {user?.avatar_url ? (
                                    <img
                                        alt="User Avatar"
                                        className={`w-9 h-9 rounded-full object-cover border ${isDark ? 'border-cyan' : 'border-primary'}`}
                                        src={user.avatar_url}
                                    />
                                ) : (
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border ${isDark ? 'bg-cyan/20 border-cyan text-cyan' : 'bg-primary/20 border-primary text-teal'}`}>
                                        {getInitials(user?.full_name)}
                                    </div>
                                )}
                                <span className={`material-symbols-outlined text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>expand_more</span>
                            </div>

                            {/* User dropdown menu */}
                            {userMenuOpen && (
                                <div className={`absolute right-0 mt-2 w-56 rounded-xl shadow-lg overflow-hidden z-50 border ${isDark ? 'bg-navy border-light/20' : 'bg-mint border-teal/20'}`}>
                                    {/* User info header */}
                                    <div className={`p-4 border-b ${isDark ? 'border-light/10' : 'border-teal/10'}`}>
                                        <p className={`text-sm font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{user?.full_name || 'Alex Morgan'}</p>
                                        <p className={`text-xs ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>{user?.email || 'alex@offtrigger.com'}</p>
                                    </div>
                                    <nav className="py-2">
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-light/70 hover:bg-light/5 hover:text-light' : 'text-darkTeal/70 hover:bg-teal/5 hover:text-darkTeal'}`}
                                        >
                                            <span className="material-symbols-outlined text-lg">person</span>
                                            Profile
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/billing'); }}
                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-light/70 hover:bg-light/5 hover:text-light' : 'text-darkTeal/70 hover:bg-teal/5 hover:text-darkTeal'}`}
                                        >
                                            <span className="material-symbols-outlined text-lg">credit_card</span>
                                            Billing
                                        </button>
                                        <button
                                            onClick={() => toggleTheme()}
                                            className={`md:hidden w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-light/70 hover:bg-light/5 hover:text-light' : 'text-darkTeal/70 hover:bg-teal/5 hover:text-darkTeal'}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="material-symbols-outlined text-lg">{isDark ? 'light_mode' : 'dark_mode'}</span>
                                                {isDark ? 'Light Mode' : 'Dark Mode'}
                                            </div>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? 'bg-cyan/20 text-cyan' : 'bg-primary/20 text-teal'}`}>
                                                {isDark ? 'DARK' : 'LIGHT'}
                                            </span>
                                        </button>
                                    </nav>
                                    <div className={`border-t ${isDark ? 'border-light/10' : 'border-teal/10'}`}>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); onLogout(); }}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-pink hover:bg-pink/10 transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-lg">logout</span>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </header>
    );
};

export default Navbar;
